(() => {
  const dayMs = 86400000;
  const key = (date) => date.toISOString().slice(0, 10);
  const normalize = (value) => String(value ?? '').trim().toLocaleLowerCase();
  function berlinDay(now, timeZone) {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23',
    }).formatToParts(now).filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
    return { date: new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day))), hour: Number(parts.hour) };
  }
  function nationalHolidays(year) {
    const a = year % 19, b = Math.floor(year / 100), c = year % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = (h + l - 7 * m + 114) % 31 + 1;
    const easter = Date.UTC(year, month - 1, day);
    return new Set([
      `${year}-01-01`, `${year}-05-01`, `${year}-10-03`, `${year}-12-25`, `${year}-12-26`,
      ...[-2, 1, 39, 50].map((offset) => key(new Date(easter + offset * dayMs))),
    ]);
  }
  function parseRules(value) {
    return String(value || '').split(/\r?\n/).filter((line) => line.trim()).map((line) => {
      const [scope, match, minText, maxText, mode = 'parcel'] = line.split('|').map((part) => part.trim());
      const min = Number(minText), max = Number(maxText);
      if (!['vendor', 'product', 'variant'].includes(scope) || !match || !/^\d+$/.test(minText || '') || !/^\d+$/.test(maxText || '') || min > max || max > 90 || !['parcel', 'freight'].includes(mode)) return null;
      return { scope, match: normalize(match), min, max, mode };
    }).filter(Boolean);
  }
  function calculate(config, now = new Date()) {
    if (!config.variant?.available) return { state: 'unavailable' };
    if (config.variant.backorder) return { state: 'request' };
    const countries = String(config.countries || 'DE').split(/[\s,;]+/).map((v) => v.toUpperCase());
    if (!countries.includes(String(config.country).toUpperCase())) return { state: 'request' };
    const rules = parseRules(config.rules);
    let rule;
    for (const scope of ['variant', 'product', 'vendor']) {
      const value = normalize(scope === 'variant' ? config.variant.id : config[scope === 'product' ? 'handle' : scope]);
      rule = rules.find((entry) => entry.scope === scope && entry.match === value);
      if (rule) break;
    }
    if (!rule && config.defaultConfirmed && Number.isInteger(config.min) && Number.isInteger(config.max) && config.min >= 0 && config.max >= config.min && config.max <= 90) {
      rule = { min: config.min, max: config.max, mode: 'parcel' };
    }
    if (!rule) return { state: 'request' };
    const local = berlinDay(now, config.timeZone || 'Europe/Berlin');
    const blocked = new Set(String(config.blockedDates || '').split(/[\s,;]+/).filter((v) => /^\d{4}-\d{2}-\d{2}$/.test(v)));
    const holidays = new Map();
    const isWorkday = (date) => {
      if ([0, 6].includes(date.getUTCDay()) || blocked.has(key(date))) return false;
      if (!config.holidays || config.country !== 'DE') return true;
      const year = date.getUTCFullYear();
      if (!holidays.has(year)) holidays.set(year, nationalHolidays(year));
      return !holidays.get(year).has(key(date));
    };
    let start = new Date(local.date);
    if (Number.isInteger(config.cutoff) && config.cutoff >= 0 && config.cutoff < 24 && local.hour >= config.cutoff) start = new Date(start.getTime() + dayMs);
    for (let guard = 0; !isWorkday(start) && guard < 370; guard++) start = new Date(start.getTime() + dayMs);
    if (!isWorkday(start)) return { state: 'request' };
    const addDays = (count) => {
      let result = new Date(start), remaining = count;
      for (let guard = 0; remaining > 0 && guard < 730; guard++) {
        result = new Date(result.getTime() + dayMs);
        if (isWorkday(result)) remaining--;
      }
      return remaining ? null : result;
    };
    const earliest = addDays(rule.min), latest = addDays(rule.max);
    if (!earliest || !latest) return { state: 'request' };
    return { state: 'estimate', earliest, latest, min: rule.min, max: rule.max, mode: rule.mode };
  }
  function formatRange(result, locale) {
    const first = result.earliest, last = result.latest;
    const options = { timeZone: 'UTC', day: 'numeric', month: 'long' };
    if (first.getUTCFullYear() !== last.getUTCFullYear()) options.year = 'numeric';
    const formatter = new Intl.DateTimeFormat(locale || 'de', options);
    if (key(first) === key(last)) return formatter.format(first);
    if (first.getUTCMonth() === last.getUTCMonth() && first.getUTCFullYear() === last.getUTCFullYear() && String(locale).startsWith('de')) return `${first.getUTCDate()}.–${formatter.format(last)}`;
    return `${formatter.format(first)} – ${formatter.format(last)}`;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { calculate, parseRules, nationalHolidays, formatRange, berlinDay };
  if (typeof customElements === 'undefined' || customElements.get('shopde-delivery-estimate')) return;
  class ShopdeDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      try { this.config = JSON.parse(this.querySelector('[data-delivery-config]').textContent); } catch { return; }
      this.render();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.sectionId !== this.config.sectionId) return;
          const source = event.data.html?.getElementById(this.id)?.querySelector('[data-delivery-config]');
          if (source) {
            try { this.config = JSON.parse(source.textContent); } catch { return; }
          } else {
            this.config.variant = event.data.variant ? { id: event.data.variant.id, available: false } : {};
          }
          this.render();
        });
      }
      this.refreshTimer = setInterval(() => this.render(), 60000);
    }
    disconnectedCallback() { this.unsubscribe?.(); clearInterval(this.refreshTimer); }
    render() {
      let result;
      try { result = calculate(this.config); } catch { result = { state: 'request' }; }
      const text = this.config.text;
      const date = this.querySelector('[data-delivery-date]');
      const duration = this.querySelector('[data-delivery-duration]');
      const note = this.querySelector('[data-delivery-note]');
      const contact = this.querySelector('[data-delivery-contact]');
      const steps = this.querySelector('[data-delivery-steps]');
      const estimated = result.state === 'estimate';
      if (steps) steps.hidden = !estimated;
      contact.hidden = estimated || result.state === 'unavailable';
      this.dataset.deliveryState = result.state;
      if (estimated) {
        try { date.textContent = formatRange(result, this.config.locale); } catch { date.textContent = formatRange(result, 'de'); }
        duration.textContent = text.duration.replace('[min]', result.min).replace('[max]', result.max);
        note.textContent = result.mode === 'freight' ? text.freight : text.note;
      } else {
        date.textContent = result.state === 'unavailable' ? text.unavailable : text.onRequest;
        duration.textContent = result.state === 'unavailable' ? text.unavailableHint : text.contactHint;
        note.textContent = text.requestNote;
      }
    }
  }
  customElements.define('shopde-delivery-estimate', ShopdeDeliveryEstimate);
})();
